'use strict';

define('vbsw/private/fetchHandler',[
  'vbsw/private/utils',
  'vbsw/private/stateCache',
  'vbsw/api/fetchHandlerPlugin',
  'vbsw/private/constants',
  'vbc/private/log',
  'vbsw/private/urlMapperClient',
  'vbsw/private/failClosedPluginError',
  'jsondiff',
], (Utils, StateCache, FetchHandlerPlugin,
  Constants, Log, UrlMapperClient, FailClosedPluginError, JsonDiff) => {
  const MAX_RETRIES = 1;
  const SERVICE_WORKER_PLUGIN_INFOS = 'vbServiceWorkerPluginInfos';
  const FETCH_HANDLER_RESPONSE_CACHE = 'vbFetchHandlerResponseCache';
  let logger;

  // used by installPlugins to determine if a plugin has changed
  const jsonDiff = JsonDiff.create({
    arrays: {
      detectMove: false,
    },
    cloneDiffValues: false,
  });

  /**
   * Handles fetch requests and interacts with plugins in the service worker.
   *
   * @class FetchHandler
   */
  class FetchHandler {
    /**
     * Creates an instance of FetchHandler.
     *
     * @param {string} [scope='/'] - The scope of the service worker.
     * @param {Object} [config={}] - Configuration object for the service worker.
     * @param {Object} [config.log] - Logger configuration.
     * @param {Object} [config.logConfig] - Log configuration for the service worker.
     * @param {string} [config.logConfig.level] - Minimum log level.
     * @param {Object} [config.requireConfig] - RequireJS configuration.
     * @param {boolean} [config.useCacheResponseWhenOfflineHeaderEnabled] - Whether to use cached responses when
     * offline.
     * @param {Function} [config.browserFetch] - Custom fetch function for the browser.
     */
    constructor(scope = '/', config = {}) {
      // set up log configuration for Service Worker thread loggers
      if (config.logConfig && config.logConfig.level) {
        Log.setMinimumLevel(config.logConfig.level);
      }
      Log.setConfig(config.log);
      logger = Log.getLogger('/vbsw/private/fetchHandler');
      logger.info('FetchHandler scope:', scope, 'config:', config);

      this.scope = scope;
      this.config = config;
      this.plugins = [];
      this.browserFetch = config.browserFetch;

      // for emulation mode and unit tests, we need to make sure the stateCache and cacheStrategy are
      // created since restore won't be called
      this.stateCache = new StateCache();

      // used to reverse-map URLs to 'vb-init-extension' information
      this.urlMapperClient = new UrlMapperClient(this.config);
    }

    /**
     * Checks if the 'useCacheResponseWhenOfflineHeaderEnabled' configuration is enabled.
     *
     * @returns {boolean} Whether the configuration is enabled.
     */
    get useCacheResponseWhenOfflineHeaderEnabled() {
      return !!(this.config && this.config.useCacheResponseWhenOfflineHeaderEnabled);
    }

    /**
     * Retrieves the configuration object for the service worker.
     *
     * @returns {Object} The configuration object.
     */
    getConfig() {
      return this.config;
    }

    /**
     * Sets up any externally provided RequireJS configuration.
     */
    setRequireConfig() {
      if (this.config.requireConfig) {
        require.config(this.config.requireConfig);
      }
    }

    /**
     * Retrieves the cached plugin information.
     *
     * @returns {Promise<Object>} A promise resolving to the cached plugin information.
     */
    getCachedPluginInfos() {
      return this.stateCache.get(SERVICE_WORKER_PLUGIN_INFOS);
    }

    /**
     * Caches the provided plugin information for future use.
     *
     * @param {Array<Object|string>} pluginInfos - An array of plugin information to cache.
     * @returns {Promise<void>} A promise that resolves when the caching is complete.
     */
    cachePluginInfos(pluginInfos) {
      return this.stateCache.put(SERVICE_WORKER_PLUGIN_INFOS, pluginInfos);
    }

    /**
     * Loads and installs a plugin at the specified index.
     *
     * @param {Object|string} pluginInfo - Information about the plugin to install. Can be a URL string or an object
     *                                     with 'url' and 'params' properties.
     * @param {number} index - The index at which to install the plugin.
     * @param {boolean} initCache - Whether to initialize the cache for the newly installed plugin.
     * @returns {Promise<void>} A promise that resolves when the plugin installation is complete.
     */
    installPlugin(pluginInfo, index, initCache) {
      let pluginUrl;
      let params;

      if (typeof pluginInfo === 'object') {
        pluginUrl = pluginInfo.url;
        params = pluginInfo.params;
      } else {
        pluginUrl = pluginInfo;
      }

      return Utils.getResource(pluginUrl).then((PluginClass) => {
        logger.info('Plugin loaded from', pluginUrl);

        // create a context object containing this fetchHandler and a namespaced stateCache
        const context = {
          fetchHandler: this,
          stateCache: this.stateCache.createNamespacedCache(pluginUrl),
        };

        const plugin = new PluginClass(context, params);
        this.plugins[index] = plugin;

        // if initCache is true, call initCache() on the newly created plugin to initialize the cache
        return initCache ? plugin.initCache() : undefined;
      }).catch((err) => {
        logger.error('Failed to load plugin from', pluginUrl, err);
      });
    }

    /**
     * Loads and installs plugins specified in pluginInfos. For example:
     *
     * [
     *   'vbsw/plugin1',
     *   {
     *     url: 'vbsw/plugin2',
     *     params:  {
     *       foo: 'bar'
     *     }
     *   }
     * ]
     *
     * Note that this method will replace all the existing installed plugins.
     *
     * @param {Array<Object|string>} pluginInfos - An array of plugin information. Can be URL strings or objects
     *                                             with 'url' and 'params' properties.
     * @param {boolean} [reload=false] - Whether to force reload all plugins.
     * @returns {Promise<void>} A promise that resolves when plugin installation is complete.
     */
    installPlugins(pluginInfos, reload = false) {
      return this.getCachedPluginInfos().then((cachedPluginInfos) => {
        const oldPluginInfos = cachedPluginInfos || pluginInfos;
        const newPluginInfos = pluginInfos || oldPluginInfos || [];
        const oldPlugins = this.plugins;
        const promises = [];
        this.plugins = new Array(newPluginInfos.length);

        for (let i = 0; i < newPluginInfos.length; i += 1) {
          const info = newPluginInfos[i];

          // look up the existing plugin whose info hasn't changed
          const index = oldPluginInfos.findIndex((oldInfo) => !jsonDiff.diff(oldInfo, info));

          // don't reuse the plugin if reload is true
          if (!reload && index !== -1) {
            this.plugins[i] = oldPlugins[index];
          }

          if (!this.plugins[i]) {
            promises.push(this.installPlugin(info, i, reload));
          }
        }

        return Promise.all(promises).then(() => this.cachePluginInfos(newPluginInfos));
      });
    }

    /**
     * Uninstalls currently installed plugins. Used for testing purposes.
     *
     * @returns {Promise<void>} A promise that resolves when uninstallation is complete.
     */
    uninstallPlugins() {
      this.plugins = [];
      return Promise.resolve();
    }

    /**
     * Activates URL mapping for the service worker.
     *
     * @returns {Promise<void>} A promise that resolves when URL mapping is activated.
     */
    activateUrlMapping() {
      return this.urlMapperClient.activate();
    }

    /**
     * Creates a new Request object.
     *
     * @param {string} url - The URL for the request.
     * @param {Object} config - Configuration for the request.
     * @returns {Request} The created Request object.
     */
    // eslint-disable-next-line class-methods-use-this
    createRequest(url, config) {
      return new Request(url, config);
    }

    /**
     * Clones the given request.
     *
     * @param {Request} request - The request to be cloned.
     * @returns {Promise<Request|null>} A promise resolving to the cloned request or null if cloning fails.
     */
    cloneRequest(request) {
      return FetchHandlerPlugin.getRequestConfig(request, this)
        .then((config) => this.createRequest(config.url, config))
        .catch((err) => {
          logger.error(err);

          // simply return null if the request cannot be cloned
          return null;
        });
    }

    /**
     * Performs a fetch operation using either the custom browserFetch function or the global fetch.
     *
     * @param {Request} request - The request to fetch.
     * @returns {Promise<Response>} A promise resolving to the fetch response.
     */
    fetch(request) {
      return this.browserFetch ? this.browserFetch.call(globalThis, request) : fetch(request);
    }

    /**
     * Wrap errors from fail-closed plugins so handleRequest can distinguish security failures from normal fetch errors.
     *
     * @param {FetchHandlerPlugin} plugin the fail-closed plugin that failed
     * @param {*} err the original plugin error
     * @returns {Error}
     */
    static createFailClosedPluginError(plugin, err) {
      return new FailClosedPluginError(plugin, err);
    }

    /**
     * Returns true if the error was raised by a fail-closed plugin.
     *
     * @param {*} error the error to check
     * @returns {boolean}
     */
    static isFailClosedPluginError(error) {
      return error instanceof FailClosedPluginError;
    }

    /**
     * Returns true if any installed plugin is configured to fail closed.
     *
     * @returns {boolean}
     */
    hasFailClosedPlugin() {
      return this.plugins.some((plugin) => plugin && plugin.failClosed);
    }

    /**
     * Invokes handleRequestHook on all installed plugins.
     *
     * @param {Request} request - The request to be handled by plugins.
     * @param {Object} client - The client for the originator of the request.
     * @returns {Promise<Request>} A promise resolving to the modified request.
     */
    handleRequestHook(request, client) {
      // execute the plugins in order by chaining the promises
      return this.plugins.reduce((promise, plugin) => promise
        .then((r) => {
          if (!plugin) {
            return r;
          }

          return Promise.resolve()
            .then(() => plugin.handleRequestHook(r, client))
            .then((r2) => r2 || r)
            .catch((err) => {
              if (plugin.failClosed) {
                throw FetchHandler.createFailClosedPluginError(plugin, err);
              }

              logger.error(err);
              // prevent a non-security plugin from breaking everyone
              return r;
            });
        }), Promise.resolve(request));
    }

    /**
     * Invokes handleResponseHook on all installed plugins. Returns true if any plugin indicates a retry is needed.
     *
     * @param {Response} response - The response to be handled by plugins.
     * @param {Request} origRequest - The original request.
     * @param {Request} request - The modified request.
     * @param {Object} client - The client for the originator of the request.
     * @returns {Promise<boolean>} A promise resolving to whether the request should be retried.
     */
    handleResponseHook(response, origRequest, request, client) {
      // execute the plugins in order by chaining the promises
      return this.plugins.reduce((promise, plugin) => promise
        .then((retry) => {
          if (!plugin) {
            return retry;
          }

          return Promise.resolve()
            .then(() => plugin.handleResponseHook(response, origRequest, request, client))
            .catch((err) => {
              if (plugin.failClosed) {
                throw FetchHandler.createFailClosedPluginError(plugin, err);
              }

              logger.error(err);
              // prevent a non-security plugin from breaking everyone
              return false;
            })
            .then((localRetry) => localRetry || retry);
        }), Promise.resolve(false));
    }

    /**
     * Invokes handleErrorHook on all installed plugins. Returns true if any plugin indicates a retry is needed.
     *
     * @param {Error} error - The error to be handled.
     * @param {Request} origRequest - The original request.
     * @param {Request} request - The modified request.
     * @param {Object} client - The client for the originator of the request.
     * @returns {Promise<boolean>} A promise resolving to whether the request should be retried.
     */
    handleErrorHook(error, origRequest, request, client) {
      // execute the plugins in order by chaining the promises
      return this.plugins.reduce((promise, plugin) => promise
        .then((retry) => (!plugin ? retry : plugin.handleErrorHook(error, origRequest, request, client)
          .catch((err) => {
            // log the error and prevent the bad plugin from breaking everyone
            logger.error(err);
            return false;
          })
          .then((localRetry) => localRetry || retry))), Promise.resolve(false));
    }

    /**
     * Handles a request by cloning it, invoking plugin hooks, fetching, and handling responses or errors.
     *
     * @param {Request} origRequest - The original request object.
     * @param {Object} client - The originating client of the request.
     * @param {boolean} skipCache - Whether to skip caching for this request.
     * @param {number} [retryCount=0] - The current retry count.
     * @returns {Promise<Response>} A promise resolving to the final response.
     */
    handleRequest(origRequest, client, skipCache, retryCount = 0) {
      return Promise.resolve().then(() => {
        // can't modify navigate or OPTIONS request so directly call browser fetch on the request
        if (origRequest.mode === 'navigate' || origRequest.method === 'OPTIONS') {
          return this.fetch(origRequest);
        }

        // For PWA, directly process the request containing the vb-use-cached-response-when-offline header, e.g.,
        // current user request or extension manager registry request, by caching the response in CacheStorage
        // when online and return the cached response when offline
        if (!skipCache && this.useCacheResponseWhenOfflineHeaderEnabled) {
          const header = origRequest.headers.get(Constants.USE_CACHED_RESPONSE_WHEN_OFFLINE);

          if (header) {
            return this.getCachedResponseWhenOffline(origRequest);
          }
        }

        let origResponse;
        let origError;
        let modifiedRequest;
        return this.cloneRequest(origRequest)
          .then((request) => {
            // if the request is null, the clone failed. Only fall back to the original request when no
            // fail-closed plugins are installed, since those plugins need a cloned request to apply security metadata.
            if (!request) {
              if (this.hasFailClosedPlugin()) {
                throw FetchHandler.createFailClosedPluginError(null, new Error('Failed to clone request'));
              }

              return this.fetch(origRequest);
            }

            return this.handleRequestHook(request, client)
              .then((r2) => {
                modifiedRequest = r2;
                return this.fetch(r2);
              })
              .then((response) => {
                origResponse = response;
                return this.handleResponseHook(response, origRequest, modifiedRequest, client);
              })
              .catch((err) => {
                origError = err;

                if (FetchHandler.isFailClosedPluginError(err)) {
                  return false;
                }

                // If the error is due to an aborted request, don't call handleErrorHook.
                if (modifiedRequest && modifiedRequest.signal && modifiedRequest.signal.aborted) {
                  return false;
                }

                logger.error(err);
                return this.handleErrorHook(err, origRequest, modifiedRequest, client);
              })
              .then((retry) => {
                if (retry) {
                  if (retryCount <= MAX_RETRIES) {
                    logger.info('Retrying request', retryCount);
                    return this.handleRequest(origRequest, client, skipCache, retryCount + 1);
                  }

                  logger.info('Max retry count reached.');
                }

                if (FetchHandler.isFailClosedPluginError(origError)) {
                  throw origError;
                }

                if (origResponse) {
                  return origResponse;
                }

                throw origError;
              });
          });
      });
    }

    /**
     * Retrieves a cached response when offline or caches the response if online.
     *
     * @param {Request} request - The request for which to retrieve or cache a response.
     * @returns {Promise<Response>} A promise resolving to the cached or fetched response.
     */
    getCachedResponseWhenOffline(request) {
      return caches.open(FETCH_HANDLER_RESPONSE_CACHE)
        .then((cache) => {
          if (!FetchHandler.isOffline && navigator.onLine) {
            return this.handleRequest(request, null, true)
              .then((response) => cache.put(request, response.clone())
                .then(() => response));
          }

          return cache.match(request)
            .then((response) => {
              if (!response) {
                throw new Error(`No cached response found for ${request.url}`);
              }
              return response.clone();
            });
        });
    }

    /**
     * Forces the fetch handler to operate in offline mode or not.
     * This is a test-only api.
     *
     * @param {boolean} flag If true, forces the fetch handler to be offline. If false, allows it to be online.
     */
    static forceOffline(flag) {
      if (Utils.isUnitTestMode() || Utils.isPuppeteerTestMode()) {
        FetchHandler.isOffline = flag;
      }
    }
  }

  // For test purposes
  // PWA tests reload the page
  FetchHandler.isOffline = (Utils.isUnitTestMode() || Utils.isPuppeteerTestMode())
    && (globalThis['vb.FetchHandler.isOffline'] === true);

  return FetchHandler;
});

